import {Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {Hierarchy, HierarchyEntity, HierarchyService} from '../../shared/hierarchy.service';
import {BehaviorSubject, forkJoin, Observable, of, Subject} from 'rxjs';
import {first, map, startWith, switchMap, takeUntil} from 'rxjs/operators';
import {
    ECMode,
    ECPodCoordinates,
    ECRepresentative,
    ECRepresentativePersonalData,
    EnergyCommunity,
    EntityType,
    UpdateECRepresentativeRequest,
    UpdateEnergyCommunityRequest
} from 'smag-commons/cfg/hierarchy';
import {can} from '../../shared/permissions';
import {
    EntityMeasureAggregationType,
    MeasureAggregationDataSource,
    Permission,
    Scope,
    TIME_UNITS,
    TimeAggregationType
} from 'smag-commons/dto/api';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {SmagApiService} from '../../api/smag-api.service';
import {MsgBoxService} from '../../shared/msg-box.service';
import {I18NService} from '../../i18n/i18n.service';
import {Router} from '@angular/router';
import * as _ from 'lodash';
import {isDefined, safeRatio} from '../../commons/commons';
import {ECAggregation, EnergyCommunityInfo} from '../../shared/ec-measure-aggregations/ec-measure-aggregations.component';
import {EconomicsConfig} from 'smag-commons/cfg';
import {ConfigurationService} from '../../shared/configuration.service';
import {DialogService} from '../../shared/dialog/dialog.service';
import {ECMembersStatistics, ECPlantsStatistics, ECPodsStatistics} from 'smag-commons/dto/eco';
import {EntityNicePathPipe} from '../../shared/shared.pipe';
import {AuthService} from 'auth-commons';
import {FormControl} from '@angular/forms';
import {addressToString, mkMode} from '../../shared/energy-community';
import {TurboObjectWithDates} from '../../api/dto';
import {StatisticsErrorType} from 'smag-commons/dto/api/statistics-errors';

const DIALOG_WIDTH = 1200;
const ASSOCIATION_DIALOG_WIDTH = 1050;
const AGGREGATION_REFRESH_FREQUENCY = 60; // seconds

@Component({
    selector: 'app-ec-dashboard',
    templateUrl: './ec-dashboard.component.html',
    styleUrls: ['./ec-dashboard.component.scss']
})
export class ECDashboardComponent implements OnInit, OnDestroy {

    private destroyed = new Subject<void>();
    private npp: EntityNicePathPipe;
    @ViewChild('deleteCommunity') private deleteCommunityDialog: TemplateRef<any>;
    @ViewChild('editCommunity') private communityEditorDialog: TemplateRef<any>;
    @ViewChild('assignRepresentative') private assignRepresentativeDialog: TemplateRef<any>;
    @ViewChild('confirmAssignationRemoval') private confirmAssignationRemovalDialog: TemplateRef<any>;
    private updateRefDialog: MatDialogRef<any, ECRepresentative | boolean>;

    readonly representativeFormControl: FormControl;
    ecResource: HierarchyEntity | null; // `null` if not an energy community
    ec: TurboObjectWithDates<EnergyCommunity> | null; // `null` if not an energy community
    ecMode: ECMode;
    ecAggregations: ECAggregation[];
    ecMembersStatistics: ECMembersStatistics;
    ecPlantsStatistics: ECPlantsStatistics;
    ecPodsStatistics: ECPodsStatistics;
    inactivePlantsCount = 0;
    inactivePodsCount = 0;
    missingProfileCount = 0;
    ecPodCoordinates: ECPodCoordinates[];
    representative: ECRepresentative;
    representatives: ECRepresentative[];
    filteredRepresentatives: Subject<ECRepresentative[]>;
    permOnReps: boolean;
    isAdvUser: boolean;
    hierarchy: Hierarchy;
    busy = false;
    ECMode = ECMode;

    // exported in HTML
    readonly AGGREGATION_REFRESH_FREQUENCY = AGGREGATION_REFRESH_FREQUENCY;
    readonly isDefined = isDefined;
    readonly addressToString = addressToString;

    constructor(private readonly apiService: SmagApiService,
                private readonly auth: AuthService,
                private readonly router: Router,
                private readonly msgBoxService: MsgBoxService,
                private readonly i18n: I18NService,
                private readonly hierarchyService: HierarchyService,
                private readonly configurationService: ConfigurationService,
                private readonly dialogService: DialogService,
                private readonly dialog: MatDialog) {
        this.npp = new EntityNicePathPipe();
        this.ecResource = null;
        this.ec = null;
        this.ecMode = null;
        this.ecAggregations = [];
        this.ecPodCoordinates = [];
        this.ecMembersStatistics = null;
        this.ecPlantsStatistics = null;
        this.ecPodsStatistics = null;
        this.representativeFormControl = new FormControl();
        this.representatives = [];
        this.filteredRepresentatives = new BehaviorSubject([]);
        this.permOnReps = false;
        this.hierarchy = null;
    }

    ngOnInit() {
        this.permOnReps = this.auth.profile.scopes?.includes(Scope.EMS);
        this.isAdvUser = this.auth.profile.scopes?.includes(Scope.ADVANCED);
        this.hierarchyService.selection.pipe(
            map(resource => resource?.type === EntityType.ENERGY_COMMUNITY && can(resource?.permissions, Permission.READ) ? resource : null),
            switchMap(ecResource => ecResource ?
                this.apiService.getEntity(ecResource.id).pipe(
                    map(ec => [ecResource, ec] as [HierarchyEntity, TurboObjectWithDates<EnergyCommunity>])
                ) :
                of([null, null] as [HierarchyEntity,  TurboObjectWithDates<EnergyCommunity>])),
            switchMap(([ecResource, ec]) => this.hierarchyService.hierarchy.pipe(
                first(),
                map(hierarchy => [ecResource, ec, hierarchy] as [HierarchyEntity, TurboObjectWithDates<EnergyCommunity>, Hierarchy]))),
            takeUntil(this.destroyed)
        ).subscribe( ([ecResource, ec, hierarchy]) => {
            this.ecResource = ecResource;
            this.ec = ec;
            this.hierarchy = hierarchy;
            this.ecAggregations = this.mkAggregations();
            if (ecResource) {
                this.loadDashboardTiles(ecResource.id);
                if (this.permOnReps || this.isAdvUser) {
                    this.apiService.findRepresentativeByNetwork(ecResource.id).subscribe(rep => this.representative = rep);
                    this.getRepresentatives();
                }
            }
        });
        this.representativeFormControl.valueChanges.pipe(
            takeUntil(this.destroyed),
            startWith(''),
            map(name => (name ? this._filter(name) : this.representatives.slice())),
        ).subscribe(this.filteredRepresentatives);
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    get communityUpdatable(): boolean {
        return can(this.ecResource.permissions, Permission.UPDATE);
    }

    get communityDeletable(): boolean {
        return can(this.ecResource.permissions, Permission.DELETE);
    }

    get representativeUpdatable(): boolean {
        if (!this.ecResource) {
            return false;
        }
        const tenantPerms = this.ecResource.parent ? this.ecResource.parent.permissions : this.hierarchy.permissions;
        return this.communityUpdatable && can(tenantPerms, Permission.UPDATE);
    }

    get hasWeatherForecast(): boolean {
        return !!this.ec.meteo_connector_id && !!this.ec.meteo_virtual_device_id;
    }

    get ecInfo(): EnergyCommunityInfo {
        return {
            mode: this.ecMode
        };
    }

    onClickWeatherForecast() {
        this.router.navigate(['/c', 'ec', 'weather-forecast'], {
            queryParamsHandling: 'preserve'
        }).then();
    }

    onClickDocuments() {
        this.router.navigate(['/c', 'ec', 'documents'], {
            queryParamsHandling: 'preserve'
        }).then();
    }

    onClickMembers() {
        this.router.navigate(['/c', 'ec', 'members'], {
            queryParamsHandling: 'preserve'
        }).then();
    }

    onClickResources() {
        this.router.navigate(['/c', 'ec', 'resources'], {
            queryParamsHandling: 'preserve'
        }).then();
    }

    onClickDeleteCommunity() {
        this.dialogService.open<any, boolean>({
            title: this.i18n.translate('Delete {0}', this.npp.transform(this.ecResource.path)),
            template: this.deleteCommunityDialog
        }).closed.then(del => {
            if (del) {
                this.deleteCommunity().then();
            }
        });
    }

    onClickEditCommunity() {
        const dialog = this.dialogService.open<any, void>({
            title: this.i18n.translate('Configuration'),
            template: this.communityEditorDialog,
            context: {
                community: _.cloneDeep(this.ec),
                hierarchyEntity: this.ecResource
            }
        });
    }

    saveCommunity(req: UpdateEnergyCommunityRequest, closeFn: () => void) {
        if (req) {
            this.updateCommunity(req).then(() => closeFn());
        } else {
            closeFn();
        }
    }

    openAssignationRemovalDialog() {
        this.dialog.open(this.confirmAssignationRemovalDialog, {
            hasBackdrop: true,
            minWidth: `${ASSOCIATION_DIALOG_WIDTH}px`
        }).afterClosed().subscribe(removeAssociation => {
            if (removeAssociation) {
                this.updateRefDialog.close(true);
            }
        });
    }

    openUpdateRefDialog() {
        this.representativeFormControl.setValue(this.representative);
        this.updateRefDialog = this.dialog.open(this.assignRepresentativeDialog, {
            hasBackdrop: true,
            minWidth: `${DIALOG_WIDTH}px`
        });
        this.updateRefDialog.afterClosed().subscribe((result: ECRepresentative | boolean) => {
            // Result can either be: ( false -> nothing happens || true -> remove current assignation || obj -> assign different rep)
            this.representativeFormControl.setValue('');
            if (!!result) {  // true or obj cases
                if (this.representative) {  // if a current rep exists, remove it
                    this.representative.network_ids = this.representative.network_ids.filter(n => n !== this.ecResource.id);
                    this.apiService.updateRepresentative(this.representative.id, this.mkUpdatedRepresentative(this.representative))
                        .then(() => {
                            if (result === true) {
                                this.msgBoxService.info(this.i18n.translate('Community representative removed'));
                            }
                            this.updateRepresentatives();
                        })
                        .catch(err => {
                            this.msgBoxService.error(this.i18n.translate('An error occurred removing the community\'s representative'), err);
                        });
                }
                if (result !== true) {  // we are adding a new representative
                    result.network_ids.push(this.ecResource.id);
                    this.apiService.updateRepresentative(result.id, this.mkUpdatedRepresentative(result))
                        .then(() => {
                            this.msgBoxService.info(this.i18n.translate('Community representative updated'));
                            this.updateRepresentatives();
                        })
                        .catch(err => {
                            this.msgBoxService.error(this.i18n.translate('An error occurred updating the community\'s representative'), err);
                        });
                }
            }
        });
    }

    getNameFrom(data: ECRepresentativePersonalData) {
        return data.first_name + ' ' + data.last_name + (data.company_name ? ' (' + data.company_name + ')' : '');
    }

    onClickResetRepresentativeSearch() {
        this.representativeFormControl.setValue(null);
    }

    representativeDisplayFn(rep: ECRepresentative): string {
        return rep ? rep.personal_data.first_name + ' ' + rep.personal_data.last_name : '';
    }

    onRepresentativeLoseFocus(focusEvent: FocusEvent) {
        // That means that the user clicked outside an autocomplete element, so we need to trigger here the fire event
        if (focusEvent.relatedTarget && (focusEvent.relatedTarget as Element).tagName !== 'MAT-OPTION') {
            if (typeof this.representativeFormControl.value !== 'object') {
                // we have lost focus on (autocomplete) input, but not because we have selected an actual option:
                // simulate the dismissal of the constraint form
                this.onClickResetRepresentativeSearch();
            }
        }
    }

    private refresh(refreshHierarchy: boolean) {
        if (refreshHierarchy) {
            this.hierarchyService.reloadHierarchy().then(result => {
                    this.hierarchyService.selection.pipe(
                        map(resource => resource?.type === EntityType.ENERGY_COMMUNITY && can(resource?.permissions, Permission.READ) ? resource : null),
                        switchMap(ecResource => ecResource ?
                            this.apiService.getEntity(ecResource.id).pipe(
                                map(ec => [ecResource, ec] as [HierarchyEntity, EnergyCommunity])
                            ) :
                            of([null, null] as [HierarchyEntity, EnergyCommunity])),
                        takeUntil(this.destroyed)
                    ).subscribe(([ecResource, ec]) => {
                        this.ecResource = ecResource;
                        this.ec = ec;
                        this.loadDashboardTiles(this.ecResource.id);
                    });
                }
            );
        } else {
            this.hierarchyService.selection.pipe(
                map(resource => resource?.type === EntityType.ENERGY_COMMUNITY && can(resource?.permissions, Permission.READ) ? resource : null),
                switchMap(ecResource => ecResource ?
                    this.apiService.getEntity(ecResource.id).pipe(
                        map(ec => [ecResource, ec] as [HierarchyEntity, EnergyCommunity])
                    ) :
                    of([null, null] as [HierarchyEntity, EnergyCommunity])),
                takeUntil(this.destroyed)
            ).subscribe(([ecResource, ec]) => {
                this.ecResource = ecResource;
                this.ec = ec;
                this.loadDashboardTiles(this.ecResource.id);
            });
        }
    }

    private loadDashboardTiles(resourceId: string) {
        // Refresh data on the right of dashboard
        this.apiService.communityMembersStatistics(resourceId)
            .subscribe(result => this.ecMembersStatistics = result);
        this.apiService.communityPlantsStatistics(resourceId)
            .subscribe(result => {
                this.ecPlantsStatistics = result;
                this.inactivePlantsCount = result.errors.filter(error => error.type === StatisticsErrorType.MISSING_PLANT_EFFECTIVE_DATE).length;
            });
        this.apiService.communityPodsStatistics(resourceId)
            .subscribe(result => {
                this.ecPodsStatistics = result;
                this.ecMode = mkMode(this.ecPodsStatistics);
                this.inactivePodsCount = result.errors.filter(error => error.type === StatisticsErrorType.MISSING_POD_ACTIVATION_DATE).length;
                this.missingProfileCount = result.undefined_inactive_count + result.undefined_active_count;
            });
        this.apiService.getPodCoordinates(resourceId)
            .then((ecPodCoordinates) => {
                this.ecPodCoordinates = ecPodCoordinates.results;
            }).catch((err) => console.error(err));
    }

    private getRepresentatives() {
        let tenantIds: string;
        if (this.ecResource) {
            if (this.ecResource.parent) {
                if (can(this.ecResource.parent.permissions, Permission.READ)) {
                    tenantIds = this.ecResource.parent.id;
                }
            } else {
                if (can(this.hierarchy.permissions, Permission.READ)) {
                    tenantIds = this.hierarchy.root;
                }
            }
            if (this.representativeUpdatable && !this.isAdvUser) {
                this.apiService.listRepresentatives(1, 50, null, null, null, null,
                    null, [tenantIds], null)
                    .subscribe(result => {
                        const ecRepresentatives = this.representatives = result.results;
                        this.filteredRepresentatives.next(ecRepresentatives);
                    });
            }
        } else {
            const ecRepresentatives = this.representatives = [];
            this.filteredRepresentatives.next(ecRepresentatives);
        }
    }

    private updateRepresentatives() {
        this.apiService.findRepresentativeByNetwork(this.ecResource.id).subscribe(rep => this.representative = rep);
        this.getRepresentatives();
    }

    private mkUpdatedRepresentative(rep: ECRepresentative): UpdateECRepresentativeRequest {
        return {
            tenant: rep.tenant,
            representative_type: rep.representative_type,
            network_ids: rep.network_ids,
            personal_data: rep.personal_data
        };
    }

    private mkAggregations(): ECAggregation[] {
        if (!this.ecResource) {
            return [];
        }
        return [
            {
                name: 'Generated energy',
                valueProvider: timeRange => this.loadPastAggregationData(EntityMeasureAggregationType.COMMUNITY_ENERGY_GENERATION, TimeAggregationType.SUM, timeRange),
                uomProvider: () => of('kWh')
            },
            {
                name: 'Effective injected energy',
                valueProvider: timeRange => this.loadPastAggregationData(EntityMeasureAggregationType.COMMUNITY_ENERGY_INJECTION, TimeAggregationType.SUM, timeRange),
                uomProvider: () => of('kWh')
            },
            {
                name: 'Consumed energy',
                valueProvider: timeRange => this.loadPastAggregationData(EntityMeasureAggregationType.COMMUNITY_ENERGY_CONSUMPTION, TimeAggregationType.SUM, timeRange),
                uomProvider: () => of('kWh')
            },
            {
                name: 'Absorbed energy',
                valueProvider: timeRange => this.loadPastAggregationData(EntityMeasureAggregationType.COMMUNITY_ENERGY_ABSORPTION, TimeAggregationType.SUM, timeRange),
                uomProvider: () => of('kWh')
            },
            {
                name: 'Self-consumed energy',
                valueProvider: timeRange => this.loadPastAggregationData(EntityMeasureAggregationType.COMMUNITY_ENERGY_SELF_CONSUMPTION, TimeAggregationType.SUM, timeRange),
                uomProvider: () => of('kWh')
            },
            {
                name: 'Self-consumption ratio',
                valueProvider: timeRange => forkJoin([
                    this.loadPastAggregationData(EntityMeasureAggregationType.COMMUNITY_ENERGY_SELF_CONSUMPTION, TimeAggregationType.SUM, timeRange),
                    this.loadPastAggregationData(EntityMeasureAggregationType.COMMUNITY_ENERGY_GENERATION, TimeAggregationType.SUM, timeRange),
                ]).pipe(
                    map(([selfConsumedEnergy, generatedEnergy]) => {
                        const ratio = safeRatio(selfConsumedEnergy, generatedEnergy);
                        return isDefined(ratio) ? ratio * 100 : null;
                    })
                ),
                uomProvider: () => of('%')
            },
            {
                name: 'Shared energy',
                valueProvider: timeRange => this.loadPastAggregationData(EntityMeasureAggregationType.COMMUNITY_SHARED_ENERGY, TimeAggregationType.SUM, timeRange),
                uomProvider: () => of('kWh')
            },
            {
                name: 'Shared energy ratio',
                valueProvider: timeRange => forkJoin([
                    this.loadPastAggregationData(EntityMeasureAggregationType.COMMUNITY_SHARED_ENERGY, TimeAggregationType.SUM, timeRange),
                    this.loadPastAggregationData(EntityMeasureAggregationType.COMMUNITY_ENERGY_INJECTION, TimeAggregationType.SUM, timeRange),
                ]).pipe(
                    map(([sharedEnergy, injectedEnergy]) => {
                        const ratio = safeRatio(sharedEnergy, injectedEnergy);
                        return isDefined(ratio) ? ratio * 100 : null;
                    })
                ),
                uomProvider: () => of('%')
            },
            {
                name: 'Saved {0}',
                nameArgs: ['CO₂'],
                valueProvider: timeRange => forkJoin([
                    this.loadPastAggregationData(EntityMeasureAggregationType.COMMUNITY_ENERGY_GENERATION, TimeAggregationType.SUM, timeRange),
                    this.configurationService.economicsConfig.pipe(first()),
                ]).pipe(
                    map(([generatedEnergy, ecoCfg]) => computeSavedCO2(generatedEnergy, ecoCfg))
                ),
                uomProvider: () => of('t')
            }
        ];
    }

    private async deleteCommunity() {
        this.busy = true;
        const name = this.ecResource.name;
        try {
            await this.apiService.deleteEnergyCommunity(this.ecResource.id);
            this.msgBoxService.info(this.i18n.translate('Community {0} deleted', name));
            await this.hierarchyService.reloadHierarchy();
            return await this.router.navigate(['/c', 'dev-dashboard']); // navigate to developer dashboard
        } catch (err) {
            this.msgBoxService.error(this.i18n.translate('Cannot delete community {0}', name), err);
        } finally {
            this.busy = false;
        }
    }

    private async updateCommunity(req: UpdateEnergyCommunityRequest): Promise<void> {
        try {
            // update parameters
            await this.apiService.updateEnergyCommunity(this.ecResource.id, req);
            // refresh hierarchy if the name is changed
            if (req.name !== this.ecResource.name) {
                this.ecResource.name = req.name; // fast refresh, waiting hierarchy reload
                this.refresh(true);
            } else {
                this.refresh(false);
            }
            this.msgBoxService.info(this.i18n.translate('Energy Community successfully updated'));
        } catch (err) {
            // EntityParametersComponent still needs to be "reset" to old values
            this.msgBoxService.error(this.i18n.translate('An error occurred while updating energy community'), err);
        }
    }

    private loadPastAggregationData(aggregationType: EntityMeasureAggregationType,
                                    timeAggregation: TimeAggregationType,
                                    timeRange: [number, number]): Observable<number> {
        let [from, to] = timeRange;
        // we round to the nearest hour to comply with economics
        from = from - (from % 3600000);
        to = to - (to % 3600000);
        return this.apiService.aggregateEntityMeasures(this.ecResource.id,
            aggregationType,
            from,
            `${Math.round((to - from) / 60000)} ${TIME_UNITS.MINUTES}`,
            `60 ${TIME_UNITS.MINUTES}`, // always 1 hour minutes for ECs
            timeAggregation,
            MeasureAggregationDataSource.CASCADE,
            true,
            true).pipe(
            map(agg => agg.values.length ? agg.values[0] : null)
        );
    }

    private _filter(name: string): ECRepresentative[] {
        if (!name || typeof name !== 'string') {
            return this.representatives;
        }
        const filterValue = name.toLowerCase();
        return this.representatives.filter(option => (option.personal_data.first_name + ' ' + option.personal_data.last_name)
            .toLowerCase().includes(filterValue)
        );
    }

    public representativeToString(rep: ECRepresentative) {
        return rep ? `${rep.personal_data.first_name} ${rep.personal_data.last_name}` : '';
    }

}

function computeSavedCO2(generatedEnergy: number, ecoCfg: EconomicsConfig) {
    return isDefined(generatedEnergy) ? generatedEnergy * ecoCfg.co2_E_ef / 1000000 : null;
}
